/**
 * @fileoverview Chat panel for instructing the mail agent (MOR-529 Phase 2).
 *
 * Wires the shared premium `ChatSheet` (the same slide-out the universal
 * agent and summary chats use — branded header, expand / pop-out, voice
 * input) to the mail agent backend. Assistant turns that produced drafts
 * get inline draft cards (each with a "Review" jump to the Drafts tab); the
 * runner's task status drives the thinking indicator.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useQuery as useTanstackQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { draftStatusMeta } from "./draftStatus";
import type { MailChatMessage } from "./types";

interface MailAgentChatPanelProps {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the Drafts folder; with a draftId, selects that draft. */
  onReviewDrafts: (draftId?: Id<"mailDrafts">) => void;
  /** Pre-fills the chat composer (used by the "Draft with Morgan" flow). */
  initialInput?: string;
}

export function MailAgentChatPanel({
  jobId,
  open,
  onOpenChange,
  onReviewDrafts,
  initialInput,
}: MailAgentChatPanelProps) {
  const rawMessages = useQuery(
    api.jobs.summarization.agents.mailAgent.publicMutations
      .listMailChatMessages,
    { jobId },
  );
  const agentStatus = useQuery(
    api.jobs.summarization.agents.mailAgent.publicMutations.getMailAgentStatus,
    { jobId },
  );
  const sendMessage = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations.sendMailChatMessage,
  );
  const startNewChat = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations.startNewMailChat,
  );

  const { data: currentUser } = useTanstackQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    const user = currentUser as {
      avatarUrl?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };
    return {
      avatarUrl: user.avatarUrl,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    };
  }, [currentUser]);

  const [sending, setSending] = useState(false);
  const working = agentStatus === "running";

  const handleSend = async (message: string) => {
    setSending(true);
    try {
      await sendMessage({ jobId, message });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to send message"));
    } finally {
      setSending(false);
    }
  };

  const handleNewChat = async () => {
    try {
      await startNewChat({ jobId });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to start a new chat"));
    }
  };

  const messages: ChatMessageData[] = useMemo(
    () =>
      (rawMessages ?? []).map((message) => ({
        id: message._id,
        role: message.role,
        content: message.content,
        createdAt: message._creationTime,
        extra:
          message.status === "error" ||
          (message.drafts && message.drafts.length > 0) ? (
            <MessageExtras message={message} onReviewDrafts={onReviewDrafts} />
          ) : undefined,
      })),
    [rawMessages, onReviewDrafts],
  );

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Chat — Mail"
      messages={messages}
      onSend={handleSend}
      onNewThread={() => void handleNewChat()}
      userInfo={userInfo}
      isLoading={rawMessages === undefined}
      isSending={sending}
      isThinking={working || sending}
      emptyMessage={
        "Ask Morgan to draft an email for this job. Every email Morgan drafts appears under For Review before anything is sent."
      }
      inputPlaceholder="What can Morgan help you with?"
      initialInput={initialInput}
      widthClassName="w-full sm:max-w-[400px]"
      showExport={false}
      showMaximize={false}
    />
  );
}

/** Inline draft cards + error note attached below an assistant turn. */
function MessageExtras({
  message,
  onReviewDrafts,
}: {
  message: MailChatMessage;
  onReviewDrafts: (draftId?: Id<"mailDrafts">) => void;
}) {
  return (
    <div className="mt-2 space-y-1.5">
      {message.status === "error" && (
        <p className="text-xs text-destructive">
          Something went wrong on this turn.
        </p>
      )}
      {message.drafts?.map((draft) => {
        const badge = draftStatusMeta(draft.status);
        return (
          <div
            key={draft.draftId}
            className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground">
                {draft.subject || "(no subject)"}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                to {draft.to.join(", ") || "(no recipients)"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span
                className={cn(
                  "rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none",
                  badge.className,
                )}
              >
                {badge.label}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() => onReviewDrafts(draft.draftId)}
              >
                Review
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
